<template>
  <section class="invite-cashback">
    <section class="invite-hero">
      <div class="invite-hero__copy">
        <div class="invite-hero__period">
          <span>{{ activityStateText }}</span>
          <p>{{ activityPeriodText }}</p>
        </div>
        <h1>
          <span>{{ campaignTitleParts[0] }}</span>
          <strong v-if="campaignTitleParts[1]">{{ campaignTitleParts[1] }}</strong>
        </h1>
        <p class="invite-hero__description">
          {{ $t('所有用户均可参与，不限邀请人数，邀请越多，返现越多。') }}
        </p>
        <div v-if="configError" class="invite-config-error">
          <span>{{ $t('活动配置加载失败') }}</span>
          <button type="button" @click="loadInviteData">{{ $t('重新加载') }}</button>
        </div>
        <div class="invite-countdown" :aria-label="countdownLabel">
          <template v-if="countdown.visible">
            <small>{{ countdownLabel }}</small>
            <template v-for="part in countdownParts">
              <strong :key="`${part.label}-value`">{{ part.value }}</strong>
              <span :key="`${part.label}-label`">{{ part.label }}</span>
            </template>
          </template>
          <small v-else>{{ activityStateText }}</small>
        </div>
        <div class="invite-hero__actions">
          <button class="invite-button invite-button--light" type="button" :disabled="!canInvite" @click="shareInvitation">
            {{ $t('立即邀请好友') }}
          </button>
          <button class="invite-button invite-button--ghost" type="button" @click="scrollToRules">
            {{ $t('查看活动规则') }}
          </button>
        </div>
      </div>

      <div class="invite-hero__rewards" aria-hidden="true">
        <div v-for="tier in campaignTiers" :key="tier.cardAmount" class="invite-reward-row">
          <div>
            <small>{{ $t('好友首次购卡') }}</small>
            <strong>{{ tier.cardAmount }}<em> U</em></strong>
          </div>
          <i class="el-icon-right" />
          <div>
            <small>{{ $t('你获得返现') }}</small>
            <strong>{{ tier.rewardAmount }}<em> U</em></strong>
          </div>
        </div>
      </div>
    </section>

    <div class="invite-layout">
      <main ref="rules" class="invite-rules">
        <article class="invite-card invite-steps">
          <h2>{{ $t('如何获得返现') }}</h2>
          <ol>
            <li v-for="(step, index) in steps" :key="step">
              <b>{{ index + 1 }}</b>
              <span>{{ step }}</span>
            </li>
          </ol>
        </article>

        <article class="invite-card invite-rule-main">
          <h2><em>01</em>{{ $t('返现规则') }}</h2>
          <p>{{ $t('邀请新用户注册，新用户首次购买点卡后，等额现金会直接返到你的返佣账户，可以提现。') }}</p>
          <div class="invite-rule-table">
            <div class="invite-rule-table__head">
              <span>{{ $t('新用户首次购买点卡') }}</span>
              <span>{{ $t('你获得返现') }}</span>
            </div>
            <div v-for="tier in campaignTiers" :key="`rule-${tier.cardAmount}`" class="invite-rule-table__row">
              <strong>{{ tier.cardAmount }}<small> U {{ $t('点卡') }}</small></strong>
              <strong>{{ tier.rewardAmount }} U</strong>
            </div>
          </div>
          <ul class="invite-rule-list">
            <li>{{ $t('新用户指活动期间通过你的邀请注册的用户。') }}</li>
            <li>{{ $t('每位新用户只返一次，按其注册后第一笔点卡订单的面额计算。') }}</li>
            <li><strong>{{ $t('第一笔订单面额不在活动档位内的，不返现，之后的订单也不再补返。') }}</strong></li>
            <li>{{ $t('只奖励直接邀请人，不向更上一级发放。') }}</li>
          </ul>
        </article>

        <div class="invite-rule-columns">
          <article class="invite-card invite-rule-small">
            <h2><em>02</em>{{ $t('返现发放') }}</h2>
            <dl>
              <div>
                <dt>A</dt>
                <dd>{{ $t('新用户订单支付成功后，返现立即进入你的返佣账户。') }}</dd>
              </div>
              <div>
                <dt>B</dt>
                <dd>{{ $t('返佣账户的余额可以提现，提现规则以 App 内公示为准。') }}</dd>
              </div>
            </dl>
          </article>
          <article class="invite-card invite-rule-small">
            <h2><em>03</em>{{ $t('其他说明') }}</h2>
            <ul>
              <li>{{ $t('活动开始前已注册的用户不算新用户。') }}</li>
              <li>{{ $t('新用户首次购买须在活动期间完成。') }}</li>
              <li>{{ $t('本活动最终解释权归平台所有。') }}</li>
            </ul>
          </article>
        </div>
      </main>

      <aside class="invite-card invite-account">
        <header>
          <h2>{{ $t('我的邀请') }}</h2>
          <!--
          <button type="button" @click="$emit('navigate', 'assets')">
            {{ $t('返佣账户 · 去提现') }} <i class="el-icon-arrow-right" />
          </button>
          -->
        </header>

        <div class="invite-account__stats">
          <div>
            <small>{{ $t('已邀请新用户') }}</small>
            <strong>{{ invitedCount }}<em>{{ $t('人') }}</em></strong>
          </div>
          <div>
            <small>{{ $t('累计返现') }}</small>
            <strong>{{ cashbackTotal }}<em>U</em></strong>
          </div>
        </div>

        <div class="invite-account__share">
          <div>
            <small>{{ $t('我的邀请码') }}</small>
            <strong>{{ inviteCode || '--' }}</strong>
            <button type="button" :disabled="!inviteCode" @click="copyInviteCode">{{ $t('复制') }}</button>
          </div>
          <div>
            <span :title="inviteLink">{{ inviteLinkDisplay || '--' }}</span>
            <button type="button" :disabled="!canInvite" @click="copyInviteLink">{{ $t('复制链接') }}</button>
          </div>
        </div>

        <button class="invite-account__primary" type="button" :disabled="!canInvite" @click="shareInvitation">
          {{ $t('立即邀请好友') }}
        </button>

        <section class="invite-record-preview">
          <header>
            <h3>{{ $t('邀请记录') }}</h3>
            <button type="button" :disabled="!records.length" @click="recordsVisible = true">
              {{ $t('全部') }} <i class="el-icon-arrow-right" />
            </button>
          </header>
          <div v-if="loading" class="invite-record-empty"><i class="el-icon-loading" /></div>
          <div v-else-if="loadError && !records.length" class="invite-record-empty invite-record-empty--error">
            <span>{{ $t('邀请数据暂时无法读取，请稍后重试') }}</span>
            <button type="button" @click="loadInviteData">{{ $t('重新加载') }}</button>
          </div>
          <div v-else-if="!records.length" class="invite-record-empty">{{ $t('暂无邀请记录') }}</div>
          <ul v-else>
            <li v-for="record in previewRecords" :key="record.key">
              <div>
                <strong>{{ record.account }}</strong>
                <small>{{ record.description }}</small>
              </div>
              <b :class="record.statusClass">{{ record.rewardText }}</b>
            </li>
          </ul>
        </section>
      </aside>
    </div>

    <transition name="invite-record-dialog">
      <div v-if="recordsVisible" class="invite-record-dialog" role="dialog" aria-modal="true" @click.self="recordsVisible = false">
        <article class="invite-record-dialog__panel">
          <header>
            <div>
              <h2>{{ $t('邀请记录') }}</h2>
              <p>{{ $t('已邀请新用户') }} {{ invitedCount }} {{ $t('人') }} · {{ $t('累计返现') }} {{ cashbackTotal }} U</p>
            </div>
            <button type="button" :aria-label="$t('关闭邀请记录')" @click="recordsVisible = false">
              <i class="el-icon-close" />
            </button>
          </header>
          <nav class="invite-record-filters" :aria-label="$t('返现状态')">
            <button
              v-for="filter in recordFilters"
              :key="filter.value"
              type="button"
              :class="{ 'is-active': recordStatus === filter.value }"
              @click="changeRecordStatus(filter.value)"
            >
              {{ filter.label }}
            </button>
          </nav>
          <div v-if="recordsLoading" class="invite-record-dialog__empty"><i class="el-icon-loading" /></div>
          <div v-else-if="loadError && !records.length" class="invite-record-dialog__empty invite-record-empty--error">
            <span>{{ loadError }}</span>
            <button type="button" @click="loadRecords(recordPage.pageNumber, recordStatus)">{{ $t('重新加载') }}</button>
          </div>
          <div v-else-if="!records.length" class="invite-record-dialog__empty">{{ $t('暂无邀请记录') }}</div>
          <div v-else class="invite-record-table">
            <div class="invite-record-table__head">
              <span>{{ $t('好友') }}</span>
              <span>{{ $t('注册时间') }}</span>
              <span>{{ $t('返现状态') }}</span>
              <span>{{ $t('返现金额') }}</span>
            </div>
            <div v-for="record in records" :key="`all-${record.key}`" class="invite-record-table__row">
              <strong :data-label="$t('好友')">{{ record.account }}</strong>
              <span :data-label="$t('注册时间')">{{ record.date }}</span>
              <span :data-label="$t('返现状态')" :class="record.statusClass">{{ record.description }}</span>
              <b :data-label="$t('返现金额')" :class="record.statusClass">{{ record.rewardText }}</b>
            </div>
          </div>
          <footer v-if="recordPage.totalPage > 1" class="invite-record-pagination">
            <button type="button" :disabled="recordPage.pageNumber <= 1 || recordsLoading" @click="changeRecordPage(-1)">
              {{ $t('上一页') }}
            </button>
            <span>{{ recordPage.pageNumber }} / {{ recordPage.totalPage }}</span>
            <button type="button" :disabled="recordPage.pageNumber >= recordPage.totalPage || recordsLoading" @click="changeRecordPage(1)">
              {{ $t('下一页') }}
            </button>
          </footer>
        </article>
      </div>
    </transition>
  </section>
</template>

<script>
import {
  getInviteCashbackConfig,
  getInviteCashbackRecords,
  getInviteCashbackSummary
} from '@/server/rebate'

function businessData(result) {
  if (!result || result.success !== true) {
    const error = new Error((result && result.message) || 'REQUEST_FAILED')
    error.statusCode = result && result.statusCode
    throw error
  }
  return result.data
}

function decimalText(value, fallback = '0') {
  const text = String(value === null || value === undefined ? '' : value).trim()
  const match = text.match(/^(-?)(\d+)(?:\.(\d+))?$/)
  if (!match) return fallback
  const sign = match[1]
  const integer = match[2].replace(/^0+(?=\d)/, '').replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  const fraction = (match[3] || '').replace(/0+$/, '')
  return `${sign}${integer}${fraction ? `.${fraction}` : ''}`
}

export default {
  name: 'InviteCashbackPanel',
  props: {
    userInfo: { type: Object, default: () => ({}) }
  },
  data() {
    return {
      campaignConfig: {},
      inviteSummary: {},
      rawRecords: [],
      recordPage: { pageNumber: 1, pageSize: 20, totalPage: 0, totalRow: 0 },
      recordStatus: 'all',
      loading: false,
      recordsLoading: false,
      configError: '',
      summaryError: '',
      recordsError: '',
      recordsVisible: false,
      serverOffsetMs: 0,
      countdown: { days: '00', hours: '00', minutes: '00', seconds: '00', visible: false },
      countdownTimer: null,
      refreshedBoundary: '',
      requestEpoch: 0,
      recordsRequestId: 0
    }
  },
  computed: {
    campaignTitleParts() {
      const title = String(this.campaignConfig.title || '').trim()
      if (!title) return [this.$t('邀新购卡'), this.$t('等额返现')]
      const parts = title.split(/\s+/)
      return parts.length > 1 ? [parts.shift(), parts.join(' ')] : [title, '']
    },
    campaignTiers() {
      const tiers = Array.isArray(this.campaignConfig.tiers) ? this.campaignConfig.tiers : []
      return tiers
        .filter(tier => tier && Number.isInteger(Number(tier.cardAmount)) && Number(tier.cardAmount) > 0)
        .map(tier => ({
          cardAmount: decimalText(tier.cardAmount, '—'),
          rewardAmount: decimalText(tier.rewardAmount, '—')
        }))
    },
    activityState() {
      return String(this.campaignConfig.state || '').toLowerCase()
    },
    activityStateText() {
      const labels = {
        disabled: '活动暂未开放',
        upcoming: '活动尚未开始',
        active: '限时活动',
        ended: '活动已结束'
      }
      return this.$t(labels[this.activityState] || (this.configError ? '活动配置加载失败' : '限时活动'))
    },
    activityPeriodText() {
      const start = this.formatBeijingDate(this.campaignConfig.startTime)
      const end = this.formatBeijingDate(this.campaignConfig.endTime)
      if (start === '—' || end === '—') return '—'
      return `${start} ${this.$t('至')} ${end} (${this.$t('北京时间')})`
    },
    countdownLabel() {
      return this.$t(this.activityState === 'upcoming' ? '距活动开始' : '距活动结束')
    },
    countdownParts() {
      return [
        { label: this.$t('天'), value: this.countdown.days },
        { label: this.$t('时'), value: this.countdown.hours },
        { label: this.$t('分'), value: this.countdown.minutes },
        { label: this.$t('秒'), value: this.countdown.seconds }
      ]
    },
    steps() {
      return [this.$t('分享邀请'), this.$t('好友注册'), this.$t('首购点卡'), this.$t('返现到账')]
    },
    inviteCode() {
      return String(this.inviteSummary.inviteCode || '').trim()
    },
    inviteLink() {
      return String(this.inviteSummary.inviteUrl || '').trim()
    },
    inviteLinkDisplay() {
      const link = this.inviteLink
      if (!link || link.length <= 48) return link
      const codeIndex = this.inviteCode ? link.lastIndexOf(this.inviteCode) : -1
      const suffixStart = codeIndex >= 0 ? Math.max(0, codeIndex - 11) : Math.max(0, link.length - 18)
      return `${link.slice(0, 25)}...${link.slice(suffixStart)}`
    },
    canInvite() {
      return Boolean(this.inviteLink)
    },
    records() {
      return this.rawRecords.map((item, index) => this.mapRecord(item, index))
    },
    previewRecords() {
      return this.records.slice(0, 4)
    },
    invitedCount() {
      const value = Number(this.inviteSummary.invitedCount)
      return Number.isInteger(value) && value >= 0 ? value : '—'
    },
    cashbackTotal() {
      return decimalText(this.inviteSummary.totalCashback, '—')
    },
    recordFilters() {
      return [
        { value: 'all', label: this.$t('全部') },
        { value: 'rewarded', label: this.$t('已返现') },
        { value: 'pending', label: this.$t('待首购') },
        { value: 'ineligible', label: this.$t('不符合条件') }
      ]
    },
    loadError() {
      return this.summaryError || this.recordsError
    },
    userIdentity() {
      const info = this.userInfo || {}
      return String(info.uid || info.id || info.user_id || info.invitation || info.username || info.mobile || '')
    }
  },
  watch: {
    userIdentity(next, previous) {
      if (!previous || next === previous) return
      this.recordsRequestId += 1
      this.inviteSummary = {}
      this.rawRecords = []
      this.recordStatus = 'all'
      this.recordPage = { pageNumber: 1, pageSize: 20, totalPage: 0, totalRow: 0 }
      this.loadInviteData()
    }
  },
  created() {
    this.countdownTimer = window.setInterval(this.updateCountdown, 1000)
    this.loadInviteData()
  },
  mounted() {
    window.addEventListener('keydown', this.handleKeydown)
    document.addEventListener('visibilitychange', this.handleVisibilityChange)
  },
  beforeDestroy() {
    this.requestEpoch += 1
    this.recordsRequestId += 1
    window.clearInterval(this.countdownTimer)
    window.removeEventListener('keydown', this.handleKeydown)
    document.removeEventListener('visibilitychange', this.handleVisibilityChange)
  },
  methods: {
    async loadInviteData() {
      const epoch = ++this.requestEpoch
      this.loading = true
      this.configError = ''
      this.summaryError = ''
      this.recordsError = ''
      await this.loadConfig(epoch)
      if (epoch !== this.requestEpoch) return
      if (window.localStorage.getItem('token')) {
        await Promise.all([this.loadSummary(epoch), this.loadRecords(1, this.recordStatus)])
      } else {
        this.inviteSummary = {}
        this.rawRecords = []
      }
      if (epoch === this.requestEpoch) this.loading = false
    },
    async loadConfig(epoch = this.requestEpoch) {
      try {
        const data = businessData(await getInviteCashbackConfig())
        if (epoch !== this.requestEpoch) return
        this.campaignConfig = data && typeof data === 'object' ? data : {}
        const serverTime = Date.parse(this.campaignConfig.serverTime)
        this.serverOffsetMs = Number.isFinite(serverTime) ? serverTime - Date.now() : 0
        this.configError = ''
        this.updateCountdown()
      } catch (error) {
        if (epoch === this.requestEpoch) this.configError = (error && error.message) || this.$t('活动配置加载失败')
      }
    },
    async loadSummary(epoch = this.requestEpoch) {
      try {
        const data = businessData(await getInviteCashbackSummary())
        if (epoch !== this.requestEpoch) return
        this.inviteSummary = data && typeof data === 'object' ? data : {}
      } catch (error) {
        if (epoch === this.requestEpoch) this.summaryError = (error && error.message) || this.$t('邀请数据暂时无法读取，请稍后重试')
      }
    },
    async loadRecords(pageNum = 1, status = this.recordStatus, retried = false) {
      const requestId = ++this.recordsRequestId
      this.recordsLoading = true
      try {
        const data = businessData(await getInviteCashbackRecords(pageNum, 20, status)) || {}
        if (requestId !== this.recordsRequestId || status !== this.recordStatus) return
        const totalPage = Math.max(0, Number(data.totalPage) || 0)
        if (!retried && totalPage > 0 && pageNum > totalPage) {
          return this.loadRecords(totalPage, status, true)
        }
        this.rawRecords = Array.isArray(data.list) ? data.list : []
        this.recordPage = {
          pageNumber: Math.max(1, Number(data.pageNumber) || pageNum),
          pageSize: Math.max(1, Number(data.pageSize) || 20),
          totalPage,
          totalRow: Math.max(0, Number(data.totalRow) || 0)
        }
        this.recordsError = ''
      } catch (error) {
        if (requestId === this.recordsRequestId) {
          this.recordsError = (error && error.message) || this.$t('邀请数据暂时无法读取，请稍后重试')
        }
      } finally {
        if (requestId === this.recordsRequestId) this.recordsLoading = false
      }
    },
    updateCountdown() {
      const targetValue = this.activityState === 'upcoming'
        ? this.campaignConfig.startTime
        : (this.activityState === 'active' ? this.campaignConfig.endTime : '')
      const target = Date.parse(targetValue)
      if (!Number.isFinite(target)) {
        this.countdown = { days: '00', hours: '00', minutes: '00', seconds: '00', visible: false }
        return
      }
      const remaining = Math.max(0, target - (Date.now() + this.serverOffsetMs))
      const seconds = Math.floor(remaining / 1000)
      this.countdown = {
        days: String(Math.floor(seconds / 86400)).padStart(2, '0'),
        hours: String(Math.floor((seconds % 86400) / 3600)).padStart(2, '0'),
        minutes: String(Math.floor((seconds % 3600) / 60)).padStart(2, '0'),
        seconds: String(seconds % 60).padStart(2, '0'),
        visible: remaining > 0
      }
      const boundaryKey = `${this.activityState}:${targetValue}`
      if (remaining === 0 && this.refreshedBoundary !== boundaryKey) {
        this.refreshedBoundary = boundaryKey
        this.loadConfig(this.requestEpoch)
      }
    },
    mapRecord(item, index) {
      const source = item && typeof item === 'object' ? item : {}
      const status = String(source.status || '').toLowerCase()
      const reasonLabels = {
        disabled: '活动已关闭',
        outside_activity: '不在活动有效期内',
        not_new_user: '非活动期内新注册用户',
        no_inviter: '无有效注册邀请关系',
        unsupported_card: '首单面额不在活动档位内',
        first_purchase_ineligible: '首单不符合返现条件'
      }
      const reason = this.$t(reasonLabels[source.reason] || '不符合活动条件')
      const cardAmount = source.cardAmount === null || source.cardAmount === undefined
        ? '—'
        : decimalText(source.cardAmount, '—')
      const rewardedAt = this.formatBeijingDate(source.rewardedAt)
      const description = status === 'rewarded'
        ? `${cardAmount === '—' ? '' : `${this.$t('首购点卡')} ${cardAmount} U · `}${this.$t('已返现')} · ${this.$t('到账时间')} ${rewardedAt}`
        : (status === 'pending' ? this.$t('待首购') : reason)
      return {
        key: `${source.nickname || 'record'}-${source.registeredAt || index}-${index}`,
        account: String(source.nickname || '—'),
        date: this.formatBeijingDate(source.registeredAt),
        description,
        rewardText: status === 'rewarded' ? `+${decimalText(source.cashbackAmount, '0')} U` : '—',
        statusClass: status === 'rewarded' ? 'is-paid' : (status === 'pending' ? 'is-pending' : 'is-none')
      }
    },
    formatBeijingDate(value) {
      if (!value) return '—'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return '—'
      const parts = new Intl.DateTimeFormat('zh-CN', {
        timeZone: 'Asia/Shanghai', year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', hourCycle: 'h23'
      }).formatToParts(date).reduce((result, part) => {
        result[part.type] = part.value
        return result
      }, {})
      return `${parts.year}-${parts.month}-${parts.day} ${parts.hour}:${parts.minute}`
    },
    changeRecordStatus(status) {
      if (status === this.recordStatus) return
      this.recordStatus = status
      this.loadRecords(1, status)
    },
    changeRecordPage(offset) {
      const current = Math.max(1, this.recordPage.pageNumber)
      const total = Math.max(0, this.recordPage.totalPage)
      const next = current + offset
      if (next < 1 || next > total || this.recordsLoading) return
      this.loadRecords(next, this.recordStatus)
    },
    handleVisibilityChange() {
      if (document.visibilityState === 'visible') {
        this.loadConfig(this.requestEpoch)
      }
    },
    scrollToRules() {
      if (this.$refs.rules) this.$refs.rules.scrollIntoView({ behavior: 'smooth', block: 'start' })
    },
    async copyText(text, successMessage) {
      if (!text) return
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(text)
        } else {
          const input = document.createElement('textarea')
          input.value = text
          input.setAttribute('readonly', '')
          input.style.position = 'fixed'
          input.style.opacity = '0'
          document.body.appendChild(input)
          input.select()
          document.execCommand('copy')
          document.body.removeChild(input)
        }
        this.$emit('toast', { message: this.$t(successMessage), type: 'success' })
      } catch (error) {
        this.$emit('toast', { message: this.$t('复制失败，请稍后重试'), type: 'error' })
      }
    },
    copyInviteCode() {
      this.copyText(this.inviteCode, '邀请码已复制')
    },
    copyInviteLink() {
      this.copyText(this.inviteLink, '邀请链接已复制')
    },
    async shareInvitation() {
      if (!this.canInvite) return
      if (navigator.share) {
        try {
          await navigator.share({ title: this.campaignConfig.title || this.$t('邀请返现'), text: this.$t('立即邀请好友'), url: this.inviteLink })
          return
        } catch (error) {
          if (error && error.name === 'AbortError') return
        }
      }
      this.copyInviteLink()
    },
    handleKeydown(event) {
      if (event.key === 'Escape') this.recordsVisible = false
    }
  }
}
</script>

<style scoped>
.invite-cashback { min-width: 0; overflow-x: hidden; color: #0d1425; padding-bottom: 28px; }
.invite-hero { min-height: 394px; padding: 44px 52px; border-radius: 24px; display: grid; grid-template-columns: minmax(0, 1.15fr) minmax(380px, .85fr); gap: 52px; align-items: center; color: #fff; background: #08080d; overflow: hidden; position: relative; }
.invite-hero::after { content: ''; position: absolute; width: 420px; height: 420px; right: -160px; top: -210px; border-radius: 50%; background: rgba(49, 109, 255, .15); filter: blur(20px); }
.invite-hero__copy, .invite-hero__rewards { position: relative; z-index: 1; }
.invite-hero__period { display: flex; align-items: center; gap: 16px; color: #9fa4b1; font-size: 13px; }
.invite-hero__period span { border: 1px solid #32343d; border-radius: 999px; padding: 6px 13px; color: #d7d9df; }
.invite-hero__period p { margin: 0; }
.invite-hero h1 { margin: 30px 0 22px; font-size: clamp(42px, 4.2vw, 62px); line-height: .98; letter-spacing: -2px; }
.invite-hero h1 span, .invite-hero h1 strong { display: block; }
.invite-hero h1 strong { margin-top: 8px; color: #4f89ff; }
.invite-hero__description { margin: 0; color: #c9cbd3; font-size: 17px; line-height: 1.7; }
.invite-config-error { margin-top: 14px; display: flex; align-items: center; gap: 12px; color: #ffb4b4; font-size: 13px; }
.invite-config-error button { padding: 0; border: 0; background: transparent; color: #fff; text-decoration: underline; cursor: pointer; }
.invite-countdown { min-height: 38px; margin-top: 20px; display: flex; align-items: center; gap: 8px; color: #9da2af; }
.invite-countdown small { margin-right: 5px; font-size: 13px; }
.invite-countdown strong { min-width: 44px; padding: 7px 9px; border-radius: 8px; background: #f6f7fa; color: #11131a; font-size: 18px; text-align: center; }
.invite-countdown span { font-size: 12px; }
.invite-hero__actions { display: flex; gap: 12px; margin-top: 24px; }
.invite-button { min-height: 48px; padding: 0 34px; border-radius: 999px; border: 1px solid #343640; font-weight: 700; cursor: pointer; }
.invite-button--light { border-color: #fff; background: #fff; color: #111218; }
.invite-button--ghost { background: transparent; color: #fff; }
.invite-button:hover { transform: translateY(-1px); }
.invite-button:disabled, .invite-account__primary:disabled { opacity: .5; cursor: not-allowed; transform: none; }
.invite-hero__rewards { padding: 6px 28px; border-radius: 20px; background: #1a1b22; box-shadow: 0 24px 70px rgba(0, 0, 0, .25); }
.invite-reward-row { min-height: 112px; display: grid; grid-template-columns: 1fr 36px 1fr; align-items: center; gap: 14px; }
.invite-reward-row + .invite-reward-row { border-top: 1px solid #2b2d35; }
.invite-reward-row > div:last-child { text-align: right; }
.invite-reward-row small { display: block; margin-bottom: 5px; color: #8e93a1; font-size: 12px; }
.invite-reward-row strong { color: #f8f9fb; font-size: 38px; line-height: 1; }
.invite-reward-row > div:last-child strong { color: #4f89ff; }
.invite-reward-row em { font-size: 22px; font-style: normal; }
.invite-reward-row i { color: #777d8c; font-size: 22px; text-align: center; }
.invite-layout { width: 100%; min-width: 0; box-sizing: border-box; margin-top: 24px; display: grid; grid-template-columns: minmax(0, 1.55fr) minmax(320px, .78fr); gap: 22px; align-items: start; }
.invite-rules { min-width: 0; display: grid; gap: 20px; scroll-margin-top: 90px; }
.invite-card { min-width: 0; max-width: 100%; box-sizing: border-box; overflow: hidden; border: 1px solid #edf0f5; border-radius: 20px; background: #fff; box-shadow: 0 10px 32px rgba(28, 43, 77, .045); }
.invite-card h2 { margin: 0; font-size: 20px; }
.invite-card h2 em { margin-right: 12px; color: #3478f6; font-style: normal; }
.invite-steps { padding: 28px 32px; }
.invite-steps ol { list-style: none; margin: 22px 0 0; padding: 0; display: grid; grid-template-columns: repeat(4, 1fr); }
.invite-steps li { min-width: 0; display: flex; align-items: center; gap: 12px; position: relative; color: #555d6f; }
.invite-steps li:not(:last-child)::after { content: ''; height: 1px; flex: 1; margin-right: 8px; background: #d9dde6; }
.invite-steps li:last-child { font-weight: 700; color: #111522; }
.invite-steps b { width: 36px; height: 36px; flex: 0 0 36px; display: grid; place-items: center; border-radius: 50%; background: #f3f5f9; color: #131621; }
.invite-steps li:last-child b { background: #101117; color: #fff; }
.invite-steps span { white-space: nowrap; }
.invite-rule-main { padding: 32px; }
.invite-rule-main > p { margin: 22px 0 18px; color: #4e5668; line-height: 1.75; }
.invite-rule-table { overflow: hidden; border: 1px solid #e5e8ef; border-radius: 14px; }
.invite-rule-table__head, .invite-rule-table__row { min-height: 54px; padding: 0 18px; display: grid; grid-template-columns: 1fr 1fr; align-items: center; }
.invite-rule-table__head { min-height: 40px; background: #f4f5f8; color: #6b7280; font-size: 12px; }
.invite-rule-table__head span:last-child, .invite-rule-table__row strong:last-child { text-align: right; }
.invite-rule-table__row + .invite-rule-table__row { border-top: 1px solid #eceef3; }
.invite-rule-table__row strong { font-size: 18px; }
.invite-rule-table__row strong:last-child { color: #3478f6; }
.invite-rule-table__row small { color: #60697a; font-size: 13px; font-weight: 500; }
.invite-rule-list { list-style: none; margin: 20px 0 0; padding: 0; display: grid; gap: 12px; color: #51596b; font-size: 13px; line-height: 1.65; }
.invite-rule-list li { padding-left: 18px; position: relative; }
.invite-rule-list li::before { content: '·'; position: absolute; left: 4px; color: #3478f6; font-weight: 900; }
.invite-rule-columns { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
.invite-rule-small { min-height: 218px; padding: 30px 32px; }
.invite-rule-small dl { margin: 20px 0 0; }
.invite-rule-small dl > div { display: grid; grid-template-columns: 22px 1fr; gap: 10px; padding: 13px 0; color: #50596a; line-height: 1.7; }
.invite-rule-small dl > div + div { border-top: 1px solid #edf0f4; }
.invite-rule-small dt { color: #111522; font-weight: 800; }
.invite-rule-small dd { margin: 0; }
.invite-rule-small ul { margin: 20px 0 0; padding: 0; list-style: none; display: grid; gap: 13px; color: #50596a; line-height: 1.6; }
.invite-account { width: 100%; min-width: 0; position: sticky; top: 86px; padding: 28px 24px 16px; }
.invite-account > header, .invite-record-preview > header { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.invite-account > header button, .invite-record-preview > header button { border: 0; padding: 0; background: transparent; color: #3978ec; font-size: 12px; cursor: pointer; }
.invite-account__stats { margin-top: 24px; display: grid; grid-template-columns: 1fr 1fr; }
.invite-account__stats small { display: block; color: #71798a; font-size: 12px; }
.invite-account__stats strong { display: block; margin-top: 8px; font-size: 28px; }
.invite-account__stats em { margin-left: 4px; color: #6f7788; font-size: 13px; font-style: normal; font-weight: 500; }
.invite-account__stats > div:last-child strong { color: #3478f6; }
.invite-account__share { width: 100%; min-width: 0; box-sizing: border-box; margin-top: 20px; padding: 5px 16px; border-radius: 16px; background: #f6f7f9; }
.invite-account__share > div { min-width: 0; min-height: 61px; display: flex; align-items: center; gap: 10px; }
.invite-account__share > div + div { border-top: 1px solid #e3e6ec; }
.invite-account__share small { display: block; color: #747c8c; font-size: 11px; }
.invite-account__share strong { min-width: 0; display: block; flex: 1 1 auto; overflow: hidden; font-size: 22px; letter-spacing: 2px; text-overflow: ellipsis; white-space: nowrap; }
.invite-account__share span { min-width: 0; flex: 1; overflow: hidden; color: #555e70; font-size: 12px; text-overflow: ellipsis; white-space: nowrap; }
.invite-account__share button { flex: 0 0 auto; min-height: 34px; padding: 0 14px; border: 1px solid #20232a; border-radius: 999px; background: #fff; color: #161922; cursor: pointer; }
.invite-account__share button:disabled, .invite-record-preview button:disabled { opacity: .45; cursor: not-allowed; }
.invite-account__primary { width: 100%; min-width: 0; box-sizing: border-box; min-height: 50px; margin-top: 16px; border: 0; border-radius: 999px; background: #0b0b10; color: #fff; font-weight: 800; cursor: pointer; }
.invite-account__primary:disabled { opacity: .45; cursor: not-allowed; }
.invite-record-preview { margin-top: 18px; padding-top: 18px; border-top: 1px solid #e8eaf0; }
.invite-record-preview h3 { margin: 0; font-size: 14px; }
.invite-record-preview ul { margin: 10px 0 0; padding: 0; list-style: none; }
.invite-record-preview li { min-height: 59px; display: flex; align-items: center; justify-content: space-between; gap: 12px; border-bottom: 1px solid #edf0f4; }
.invite-record-preview li div { min-width: 0; }
.invite-record-preview li strong { display: block; font-size: 12px; }
.invite-record-preview li small { display: block; margin-top: 5px; overflow: hidden; color: #757d8d; font-size: 11px; text-overflow: ellipsis; white-space: nowrap; }
.invite-record-preview li b { flex: 0 0 auto; font-size: 12px; }
.is-paid { color: #3478f6 !important; }
.is-pending { color: #8a909c !important; }
.is-none { color: #a0a5af !important; }
.invite-record-empty { min-height: 120px; display: grid; place-items: center; color: #8a92a2; font-size: 13px; text-align: center; }
.invite-record-empty--error { gap: 8px; align-content: center; }
.invite-record-empty--error button { border: 0; background: transparent; color: #3478f6; cursor: pointer; }
.invite-record-dialog { position: fixed; inset: 0; z-index: 2100; padding: 30px; display: grid; place-items: center; background: rgba(9, 14, 25, .56); backdrop-filter: blur(5px); }
.invite-record-dialog__panel { width: min(820px, 100%); max-height: min(720px, calc(100vh - 60px)); overflow: auto; border-radius: 22px; background: #fff; box-shadow: 0 30px 80px rgba(4, 12, 28, .25); }
.invite-record-dialog__panel > header { position: sticky; top: 0; z-index: 1; padding: 24px 28px; display: flex; align-items: center; justify-content: space-between; border-bottom: 1px solid #e8ebf1; background: #fff; }
.invite-record-dialog__panel h2 { margin: 0; font-size: 22px; }
.invite-record-dialog__panel p { margin: 7px 0 0; color: #747c8d; font-size: 13px; }
.invite-record-dialog__panel > header button { width: 38px; height: 38px; border: 1px solid #e2e6ed; border-radius: 10px; background: #fff; cursor: pointer; }
.invite-record-filters { padding: 18px 28px 6px; display: flex; flex-wrap: wrap; gap: 8px; }
.invite-record-filters button { min-height: 34px; padding: 0 14px; border: 1px solid #e4e8ef; border-radius: 999px; background: #fff; color: #646d7f; cursor: pointer; }
.invite-record-filters button.is-active { border-color: #3478f6; background: #3478f6; color: #fff; }
.invite-record-dialog__empty { min-height: 220px; padding: 24px; display: grid; place-items: center; color: #8a92a2; text-align: center; }
.invite-record-table { padding: 8px 28px 24px; }
.invite-record-table__head, .invite-record-table__row { min-height: 58px; display: grid; grid-template-columns: 1.15fr 1fr 1.55fr .8fr; gap: 14px; align-items: center; }
.invite-record-table__head { color: #7a8292; font-size: 12px; }
.invite-record-table__row { border-top: 1px solid #edf0f4; font-size: 13px; }
.invite-record-table__row b { text-align: right; }
.invite-record-pagination { padding: 0 28px 24px; display: flex; align-items: center; justify-content: center; gap: 18px; color: #687185; font-size: 13px; }
.invite-record-pagination button { min-width: 76px; min-height: 34px; border: 1px solid #e0e5ed; border-radius: 9px; background: #fff; color: #3478f6; cursor: pointer; }
.invite-record-pagination button:disabled { color: #adb4c1; cursor: not-allowed; }
.invite-record-dialog-enter-active, .invite-record-dialog-leave-active { transition: opacity .2s ease; }
.invite-record-dialog-enter-active .invite-record-dialog__panel, .invite-record-dialog-leave-active .invite-record-dialog__panel { transition: transform .2s ease; }
.invite-record-dialog-enter, .invite-record-dialog-leave-to { opacity: 0; }
.invite-record-dialog-enter .invite-record-dialog__panel, .invite-record-dialog-leave-to .invite-record-dialog__panel { transform: translateY(16px) scale(.98); }

@media (max-width: 1100px) {
  .invite-hero { grid-template-columns: 1fr .9fr; gap: 28px; padding: 38px; }
  .invite-layout { grid-template-columns: 1.35fr .8fr; }
  .invite-steps { padding: 24px; }
  .invite-steps li { gap: 8px; font-size: 12px; }
}

@media (max-width: 900px) {
  .invite-hero { grid-template-columns: 1fr; }
  .invite-hero__rewards { display: grid; grid-template-columns: 1fr 1fr; }
  .invite-reward-row + .invite-reward-row { border-top: 0; border-left: 1px solid #2b2d35; padding-left: 24px; }
  .invite-layout { grid-template-columns: minmax(0, 1fr); }
  .invite-account { position: static; grid-row: 1; }
}

@media (max-width: 640px) {
  .invite-cashback { margin: 0 -12px; padding-bottom: 12px; }
  .invite-hero { min-height: auto; padding: 28px 20px; border-radius: 0 0 22px 22px; gap: 28px; }
  .invite-hero__period { align-items: flex-start; gap: 10px; font-size: 11px; line-height: 1.5; }
  .invite-hero__period span { flex: 0 0 auto; padding: 4px 9px; }
  .invite-hero h1 { margin: 24px 0 16px; font-size: 42px; }
  .invite-hero__description { font-size: 14px; }
  .invite-countdown { flex-wrap: wrap; gap: 6px; }
  .invite-countdown small { width: 100%; }
  .invite-countdown strong { min-width: 38px; font-size: 15px; }
  .invite-hero__actions { display: grid; grid-template-columns: 1fr 1fr; }
  .invite-button { min-height: 44px; padding: 0 12px; font-size: 13px; }
  .invite-hero__rewards { display: block; padding: 2px 18px; }
  .invite-reward-row { min-height: 90px; }
  .invite-reward-row + .invite-reward-row { border-left: 0; border-top: 1px solid #2b2d35; padding-left: 0; }
  .invite-reward-row strong { font-size: 28px; }
  .invite-layout { width: auto; margin: 14px 12px 0; gap: 14px; }
  .invite-card { border-radius: 16px; }
  .invite-account { padding: 22px 18px 12px; }
  .invite-steps, .invite-rule-main, .invite-rule-small { padding: 22px 18px; }
  .invite-steps ol { grid-template-columns: 1fr 1fr; gap: 18px 10px; }
  .invite-steps li:nth-child(2)::after { display: none; }
  .invite-rule-columns { grid-template-columns: 1fr; gap: 14px; }
  .invite-rule-small { min-height: 0; }
  .invite-record-dialog { align-items: end; padding: 0; }
  .invite-record-dialog__panel { width: 100%; max-height: 82vh; border-radius: 22px 22px 0 0; }
  .invite-record-dialog__panel > header { padding: 20px; }
  .invite-record-filters { padding: 14px 20px 6px; }
  .invite-record-table { padding: 4px 20px 24px; }
  .invite-record-table__head { display: none; }
  .invite-record-table__row { padding: 15px 0; grid-template-columns: 1fr 1fr; gap: 8px 12px; }
  .invite-record-table__row > *::before { content: attr(data-label); display: block; margin-bottom: 4px; color: #8a92a2; font-size: 10px; font-weight: 500; }
  .invite-record-table__row b { text-align: left; }
}
</style>
